import { action, computed, makeObservable } from 'mobx'

import { IUploadLog, UploadLogFilterType } from '~/client/graph'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import KnownTranslatorKeys from '~/client/src/shared/localization/knownTranslatorKeys'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { RESET_ALL_FILTERS } from '~/client/src/shared/stores/EventStore/eventConstants'
import EventContext from '~/client/src/shared/stores/EventStore/EventContext'
import UIFilterInfo from '~/client/src/shared/stores/substates/UIFilterInfo'
import SuperFilterStore, {
  IOption,
  IRootOption,
} from '~/client/src/shared/stores/ui/SuperFilter.store'
import Guard from '~/client/src/shared/utils/Guard'

import AttachmentsStore from '../../../../Attachments.store'

export type ISourceMap = { [optionId: string]: string[] }

export default class BaseAttachmentFilterStore extends SuperFilterStore {
  public get totalHint(): string {
    return Localization.translator.totalAttachments
  }

  public constructor(
    public readonly type: UploadLogFilterType,
    protected readonly state: DesktopInitialState,
    protected readonly getSourceMap: () => ISourceMap,
    protected readonly attachmentsStore: AttachmentsStore,
    protected readonly onShowChanged: (isShown: boolean, type: string) => void,
    protected readonly fieldsMap: { [filterType: string]: UIFilterInfo },
    getTypeCaption: (type: string) => string,
    private readonly getOptionName?: (
      optionId: string,
      filterType: string,
    ) => string,
    private readonly filterKeyOnStorage?: string,
    private readonly getCountsForOption?: (
      filterType: string,
      optionKey: string,
    ) => { viewable: number; total: number },
  ) {
    super(
      fieldsMap[type],
      type,
      state,
      KnownTranslatorKeys.seeXAttachments,
      getTypeCaption,
      onShowChanged,
    )

    makeObservable(this)

    Guard.requireAll({ type, state, getSourceMap, attachmentsStore })
  }

  @computed
  protected get sourceMap(): ISourceMap {
    return this.getSourceMap()
  }

  @action.bound
  public onFilterActionRequest(eventContext: EventContext) {
    const [eventType] = eventContext.event
    switch (eventType) {
      case RESET_ALL_FILTERS:
        return this.setInitialFilterValues()
    }
  }

  public getCroppedAmount(option: IOption) {
    if (this.type === UploadLogFilterType.Formtype) {
      return Object.keys(this.sourceMap)
        .filter(key => this.getOptionName?.(key, this.type) === option.name)
        .reduce((acc, key) => {
          const s = this.getCountsForOption?.(this.type, key)
          return acc + (s.viewable || 0)
        }, 0)
    }
    return this.getCountsForOption?.(this.type, option.id)?.viewable
  }

  public getTotalAmount(option: IOption) {
    if (this.type === UploadLogFilterType.Formtype) {
      return Object.keys(this.sourceMap)
        .filter(key => this.getOptionName?.(key, this.type) === option.name)
        .reduce((acc, key) => {
          const s = this.getCountsForOption?.(this.type, key)
          return acc + (s.total || 0)
        }, 0)
    }
    return this.getCountsForOption?.(this.type, option.id)?.total
  }

  @computed
  public get optionsTree(): IRootOption[] {
    const sourceMap = this.sourceMap
    const options = Object.keys(sourceMap)
    let processedOptions: IOption[] = options.map(option => {
      const counts = this.getCountsForOption?.(this.type, option) || {
        viewable: 0,
        total: 0,
      }

      const optionObj: IOption = {
        id: option,
        name: this.getOptionName?.(option, this.type) || option,
        instancesIds: sourceMap[option] || [],
      }

      if (counts.total > 0) {
        optionObj.croppedAmount = counts.viewable
        optionObj.totalAmount = counts.total
      }

      return optionObj
    })

    if (this.type === UploadLogFilterType.Formtype) {
      processedOptions = this.mergeOptionsByName(processedOptions)
    }

    return [
      {
        id: this.type,
        name: this.typeCaption,
        options: processedOptions,
      },
    ]
  }

  private getMergerdCounts(options: IOption[]): {
    viewable: number
    total: number
  } {
    return options.reduce(
      (acc, option) => {
        return {
          viewable: acc.viewable + (option.croppedAmount || 0),
          total: acc.total + (option.totalAmount || 0),
        }
      },
      { viewable: 0, total: 0 },
    )
  }

  private mergeOptionsByName(options: IOption[]): IOption[] {
    const optionsByName = new Map<string, IOption[]>()

    options.forEach(option => {
      const existingGroup = optionsByName.get(option.name) || []
      existingGroup.push(option)
      optionsByName.set(option.name, existingGroup)
    })

    const mergedOptions: IOption[] = []
    optionsByName.forEach((optionsGroup, displayName) => {
      if (optionsGroup.length === 1) {
        mergedOptions.push(optionsGroup[0])
      } else {
        const mergedInstancesIds = optionsGroup.reduce(
          (acc, option) => acc.concat(option.instancesIds),
          [] as string[],
        )

        const totalCroppedAmount = optionsGroup.reduce(
          (sum, option) => sum + (option.croppedAmount || 0),
          0,
        )
        const totalAmount = optionsGroup.reduce(
          (sum, option) => sum + (option.totalAmount || 0),
          0,
        )

        const mergedOption: IOption = {
          id: displayName,
          name: displayName,
          instancesIds: mergedInstancesIds,
        }

        if (totalAmount > 0) {
          mergedOption.croppedAmount = totalCroppedAmount
          mergedOption.totalAmount = totalAmount
        }

        mergedOptions.push(mergedOption)
      }
    })

    return mergedOptions
  }

  public get isActive(): boolean {
    return this.areOptionsSelected
  }

  protected getHandleButtonCaption(): string {
    if (!this.isHandleButtonActive) {
      return this.filterCaption
    }

    const selectedOptionsAmount = this.selectedOptions.size
    switch (true) {
      case selectedOptionsAmount > 1:
        return `${this.filterCaption} (${selectedOptionsAmount})`
      case selectedOptionsAmount === 1:
        return this.getSafeSingleModeCaption()
      default:
        return this.filterCaption
    }
  }

  private getSafeSingleModeCaption(): string {
    const optionId = this.selectedOptions.keys().next().value
    const rootOption = this.optionsTree.find(ro =>
      ro.options?.some(o => o.id === optionId),
    )

    if (!rootOption || !rootOption.options) {
      return `${this.filterCaption}: ${
        optionId || Localization.translator.unknown
      }`
    }

    const option = rootOption.options.find(o => o.id === optionId)
    if (!option) {
      return `${this.filterCaption}: ${
        optionId || Localization.translator.unknown
      }`
    }

    const optionCaption = option.shortName || option.name
    return `${rootOption.name}: ${optionCaption}`
  }

  public setInitialFilterValues() {
    if (!this.filter) {
      return
    }

    this.clickOnDeselectAll()
    this.clickOnApply()
  }

  @action.bound
  public clickOnApply() {
    this.handleApply()
  }

  public filterOptionInstances = (ids: string[]) => {
    return (this.allFilteredInstances as Array<IUploadLog>).filter(i =>
      ids.includes(i.id),
    )
  }

  @computed
  private get allFilteredInstances(): IUploadLog[] {
    const { getFilteredCollectionExcludeFilter } = this.attachmentsStore
    return getFilteredCollectionExcludeFilter([this.type])
  }

  private get areOptionsSelected(): boolean {
    return (
      this.selectedOptions.size &&
      this.allOptionIds.some(optionId => !this.selectedOptions.get(optionId))
    )
  }

  public get applyButtonLabel(): string {
    if (!this.selectedOptions.size) {
      return Localization.translator.disableFilter
    }

    return Localization.translator.apply
  }
}
